import {
  createSlice,
} from '@reduxjs/toolkit';

import { oauth } from '../utils';
import { debounce } from 'lodash';

const slice = createSlice({
  name: 'promostandards_invoices',
  initialState: {
    data: [],
    loading: 'idle',
  },
  reducers: {
    loading(state) {
      if (state.loading === 'idle') {
        state.loading = 'pending';
      }
    },
    received(state, action) {
      if (state.loading === 'pending') {
        const newInvoices = action.payload;
        const newPurchaseOrderIds = newInvoices.map(inv => inv.purchase_order_id);

        const newIdSet = new Set(newPurchaseOrderIds);
        state.data = state.data.filter(existing =>
          !newIdSet.has(existing.purchase_order_id)
        );

        state.data.push(...newInvoices);
        state.loading = 'idle';
      }
    },
    invoiceConfirmed(state, action) {
      if (state.loading === 'pending') {
        const confirmedInvoice = action.payload.promostandards_invoice;

        state.data = state.data.filter(invoice =>
          invoice.purchase_order_id !== confirmedInvoice.purchase_order_id
        );

        state.data.push(confirmedInvoice);
        state.loading = 'idle';
      }
    },
    reset(state) {
      state.data = [];
      state.loading = 'idle';
    },
  }
});

export const {
  loading,
  received,
  invoiceConfirmed,
  reset,
} = slice.actions;

const baseUrl = 'purchaseorder';

let batchQueue = new Set();

/**
* Process the batched requests
*/
const processBatch = async (dispatch) => {
  if (batchQueue.size === 0) return;

  const purchase_order_ids = Array.from(batchQueue);
  batchQueue.clear();

  try {
    dispatch(loading());
    const res = await oauth('GET', `${baseUrl}/get-invoices`, { purchase_order_ids });
    dispatch(received(res.json.promostandards_invoices));
  } catch (error) {
    console.error('Failed to get promostandards invoices');
  }
};

// Debounced batch processor
const debouncedProcessBatch = debounce((dispatch) => {
  processBatch(dispatch);
}, 200);

/**
* Get promostandards invoices - with automatic batching
*/
export const getPromostandardsInvoices = (purchase_order_ids) => (dispatch, getState) => {
  const ids = Array.isArray(purchase_order_ids) ? purchase_order_ids : [purchase_order_ids];
  ids.forEach(id => batchQueue.add(id));
  debouncedProcessBatch(dispatch);
};

/**
* Confirm promostandards invoice
*/
export const confirmPromostandardsInvoice = (purchase_order_id) => async (dispatch) => {
  dispatch(loading());
  try {
    const res = await oauth('PUT', `${baseUrl}/${purchase_order_id}`, {
      actionName: 'confirm-po-invoice',
      purchase_order_id,
    });
    dispatch(invoiceConfirmed(res.json));
    return res.json;
  } catch (error) {
    dispatch(received([]));
    console.error('Failed to confirm promostandards invoice');
  }
};

/**
* Reset promostandards invoices data
*/
export const resetPromostandardsInvoices = () => (dispatch) => {
  dispatch(reset());
};

export default slice.reducer;
